import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Smile, 
  ShoppingBag, 
  Fuel, 
  Gift, 
  Utensils,
  CreditCard,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface Subscription {
  id: string;
  status: string;
  monthlyAmount: string;
  subsidyAmount: string;
  netAmount: string;
  requestedAt: string;
  activatedAt?: string;
  plan: {
    id: string;
    name: string;
    category: string;
    description?: string;
  };
  dependents: Array<{
    id: string;
    name: string;
    relationship: string;
    isActive: boolean;
  }>;
  cards: Array<{
    id: string;
    cardNumber: string;
    holderName: string;
    isActive: boolean;
  }>;
}

const categoryIcons: Record<string, React.ReactNode> = {
  HEALTH: <Heart className="w-5 h-5" />,
  DENTAL: <Smile className="w-5 h-5" />,
  SUPERMARKET: <ShoppingBag className="w-5 h-5" />,
  GAS_STATION: <Fuel className="w-5 h-5" />,
  GIFT_CARD: <Gift className="w-5 h-5" />,
  MEAL: <Utensils className="w-5 h-5" />,
};

const categoryLabels: Record<string, string> = {
  HEALTH: 'Plano de Saúde',
  DENTAL: 'Plano Odontológico',
  SUPERMARKET: 'Rede de Supermercados',
  GAS_STATION: 'Rede de Postos',
  GIFT_CARD: 'Vale Presente',
  MEAL: 'Vale Refeição',
};

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode }> = {
  PENDING_APPROVAL: { label: 'Aguardando Aprovação', variant: 'warning', icon: <Clock className="w-4 h-4" /> },
  ACTIVE: { label: 'Ativo', variant: 'success', icon: <CheckCircle className="w-4 h-4" /> },
  SUSPENDED: { label: 'Suspenso', variant: 'error', icon: <AlertCircle className="w-4 h-4" /> },
  CANCELLED: { label: 'Cancelado', variant: 'error', icon: <XCircle className="w-4 h-4" /> },
};

export function MySubscriptions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelModal, setCancelModal] = useState<Subscription | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const { data: subscriptions = [], isLoading } = useQuery<Subscription[]>({
    queryKey: ['my-subscriptions'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.mySubscriptions);
      return response.data;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.delete(ENDPOINTS.benefits.cancelSubscription(id), {
        data: { reason },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-subscriptions'] });
      setCancelModal(null);
      setCancelReason('');
    },
  });

  const activeSubscriptions = subscriptions.filter(s => s.status === 'ACTIVE');
  const pendingSubscriptions = subscriptions.filter(s => s.status === 'PENDING_APPROVAL');
  const otherSubscriptions = subscriptions.filter(s => !['ACTIVE', 'PENDING_APPROVAL'].includes(s.status));

  const totalMonthly = activeSubscriptions.reduce(
    (sum, s) => sum + Number(s.netAmount),
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Minhas Adesões</h1>
          <p className="text-surface-400 mt-1">
            Gerencie seus benefícios e adesões
          </p>
        </div>
        <Button onClick={() => navigate('/banking/benefits')}>
          <Gift className="w-4 h-4 mr-2" />
          Ver Catálogo
        </Button>
      </div>

      {/* Summary Card */}
      {activeSubscriptions.length > 0 && (
        <Card className="bg-gradient-to-r from-primary-500/20 to-accent-500/20 border-primary-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-surface-400 text-sm">Total Mensal</p>
              <p className="text-3xl font-bold text-white">
                {formatCurrency(totalMonthly / 100)}
              </p>
              <p className="text-surface-400 text-sm mt-1">
                {activeSubscriptions.length} benefício(s) ativo(s)
              </p>
            </div>
            <div className="text-right">
              <p className="text-surface-400 text-sm">Próximo desconto</p>
              <p className="text-lg font-semibold text-white">
                Dia 05 do próximo mês
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-surface-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-surface-700 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-surface-700 rounded w-1/4" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && subscriptions.length === 0 && (
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Você ainda não possui adesões
          </h3>
          <p className="text-surface-400 mb-6">
            Explore nosso catálogo de benefícios e faça sua primeira adesão
          </p>
          <Button onClick={() => navigate('/banking/benefits')}>
            Ver Benefícios Disponíveis
          </Button>
        </Card>
      )}

      {/* Pending Subscriptions */}
      {pendingSubscriptions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Aguardando Aprovação
          </h2>
          {pendingSubscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              onCancel={() => setCancelModal(sub)}
            />
          ))}
        </div>
      )}

      {/* Active Subscriptions */}
      {activeSubscriptions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            Benefícios Ativos
          </h2>
          {activeSubscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              onCancel={() => setCancelModal(sub)}
              onViewCard={() => navigate(`/banking/my-subscriptions/${sub.id}`)}
            />
          ))}
        </div>
      )}

      {/* Other Subscriptions */}
      {otherSubscriptions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Histórico</h2>
          {otherSubscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              showActions={false}
            />
          ))}
        </div>
      )}

      {/* Cancel Modal */}
      <Modal
        isOpen={!!cancelModal}
        onClose={() => {
          setCancelModal(null);
          setCancelReason('');
        }}
        title="Cancelar Adesão"
      >
        {cancelModal && (
          <div className="space-y-4">
            <p className="text-surface-300">
              Tem certeza que deseja cancelar sua adesão ao benefício{' '}
              <strong className="text-white">{cancelModal.plan.name}</strong>?
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-400 text-sm">
                ⚠️ O cancelamento pode estar sujeito a multa contratual dependendo do tempo de permanência.
              </p>
            </div>
            <Input
              label="Motivo do cancelamento"
              placeholder="Informe o motivo..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setCancelModal(null);
                  setCancelReason('');
                }}
              >
                Voltar
              </Button>
              <Button
                variant="danger"
                onClick={() => cancelMutation.mutate({
                  id: cancelModal.id,
                  reason: cancelReason,
                })}
                disabled={!cancelReason || cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SubscriptionCard({
  subscription,
  onCancel,
  onViewCard,
  showActions = true,
}: {
  subscription: Subscription;
  onCancel?: () => void;
  onViewCard?: () => void;
  showActions?: boolean;
}) {
  const status = statusConfig[subscription.status] || statusConfig.PENDING_APPROVAL;

  return (
    <Card className="hover:ring-1 hover:ring-surface-600 transition-all">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="w-12 h-12 rounded-lg bg-surface-700 flex items-center justify-center text-primary-400">
          {categoryIcons[subscription.plan.category] || <Gift className="w-6 h-6" />}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">
              {subscription.plan.name}
            </h3>
            <Badge variant={status.variant} size="sm">
              {status.icon}
              <span className="ml-1">{status.label}</span>
            </Badge>
          </div>
          <p className="text-surface-400 text-sm">
            {categoryLabels[subscription.plan.category] || subscription.plan.category}
          </p>
          
          {/* Details */}
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className="text-surface-400">
              Valor: <span className="text-white font-medium">
                {formatCurrency(Number(subscription.netAmount) / 100)}/mês
              </span>
            </span>
            {Number(subscription.subsidyAmount) > 0 && (
              <span className="text-green-400">
                Subsídio: {formatCurrency(Number(subscription.subsidyAmount) / 100)}
              </span>
            )}
            {subscription.dependents.filter(d => d.isActive).length > 0 && (
              <span className="text-surface-400 flex items-center gap-1">
                <Users className="w-4 h-4" />
                {subscription.dependents.filter(d => d.isActive).length} dependente(s)
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-2">
            {subscription.status === 'ACTIVE' && onViewCard && (
              <Button variant="secondary" size="sm" onClick={onViewCard}>
                <CreditCard className="w-4 h-4 mr-1" />
                Ver Cartão
              </Button>
            )}
            {['ACTIVE', 'PENDING_APPROVAL'].includes(subscription.status) && onCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default MySubscriptions;
